/**
 * Spirify API Service
 * Centralized API calls to backend
 */

// Utiliser l'URL du backend selon l'environnement
const getAPIURL = () => {
  // Si en développement local (localhost), utiliser le backend sur port 8001
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8001/api';
  }
  // En production/preview, utiliser l'URL relative car le serveur redirige /api vers backend
  return `${window.location.origin}/api`;
};

const API_URL = getAPIURL();

// Token management
export const getToken = () => localStorage.getItem('spirify_token');
export const setToken = (token) => localStorage.setItem('spirify_token', token);
export const removeToken = () => localStorage.removeItem('spirify_token');
export const isAuthenticated = () => !!getToken();

// Generic API call wrapper
const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const config = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);
    
    // Get response as text first
    const text = await response.text();
    
    // Parse JSON if possible
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (e) {
      data = { detail: text || 'Server error' };
    }
    
    if (response.status === 401) {
      removeToken();
      window.location.href = '/login';
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      throw new Error(data.detail || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// ==================== AUTH API ====================

export const authAPI = {
  signup: async (email, username, password) => {
    const data = await apiCall('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, username, password }),
    });
    
    if (data.access_token) {
      setToken(data.access_token);
    }
    
    return data;
  },

  login: async (email, password) => {
    const data = await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (data.access_token) {
      setToken(data.access_token);
    }
    
    return data;
  },

  logout: () => {
    removeToken();
    window.location.href = '/';
  },

  getCurrentUser: async () => {
    return await apiCall('/auth/me');
  },
};

// ==================== COURSES API ====================

export const coursesAPI = {
  getAll: async () => {
    return await apiCall('/courses');
  },

  getBySlug: async (slug) => {
    return await apiCall(`/courses/${slug}`);
  },

  getLessons: async (courseSlug) => {
    return await apiCall(`/courses/${courseSlug}/lessons`);
  },

  getLesson: async (courseSlug, lessonId) => {
    return await apiCall(`/courses/${courseSlug}/lessons/${lessonId}`);
  },

  getExercises: async (courseSlug, lessonId) => {
    return await apiCall(`/courses/${courseSlug}/lessons/${lessonId}/exercises`);
  },
};

// ==================== PROGRESS API ====================

// Helper to get active profile ID from localStorage
const getActiveProfileId = () => {
  try {
    const profile = localStorage.getItem('spirify_active_profile');
    if (profile) {
      const parsed = JSON.parse(profile);
      return parsed.id;
    }
  } catch (e) {
    console.warn('Failed to get active profile:', e);
  }
  return null;
};

export const progressAPI = {
  submitAnswer: async (lessonId, exerciseId, answer, language = 'fr') => {
    const profileId = getActiveProfileId();
    return await apiCall('/progress/submit', {
      method: 'POST',
      body: JSON.stringify({
        lesson_id: lessonId,
        exercise_id: exerciseId,
        answer: answer,
        language: language,
        profile_id: profileId,  // Family Mode support
      }),
    });
  },

  getUserProgress: async () => {
    const profileId = getActiveProfileId();
    const query = profileId ? `?profile_id=${profileId}` : '';
    return await apiCall(`/progress/user${query}`);
  },

  restoreHeart: async () => {
    const profileId = getActiveProfileId();
    const query = profileId ? `?profile_id=${profileId}` : '';
    return await apiCall(`/progress/restore-heart${query}`, {
      method: 'POST',
    });
  },
  
  getCompletedLessons: async (courseSlug) => {
    const profileId = getActiveProfileId();
    const query = profileId ? `?profile_id=${profileId}` : '';
    return await apiCall(`/progress/completed-lessons/${courseSlug}${query}`);
  },
  
  completeLesson: async (lessonId, correctAnswers, totalQuestions) => {
    const profileId = getActiveProfileId();
    const query = profileId ? `?profile_id=${profileId}` : '';
    return await apiCall(`/progress/complete-lesson/${lessonId}${query}`, {
      method: 'POST',
      body: JSON.stringify({
        correct_answers: correctAnswers,
        total_questions: totalQuestions,
      }),
    });
  },
  
  getProfileStats: async (profileId) => {
    const effectiveProfileId = profileId || getActiveProfileId();
    if (!effectiveProfileId) {
      throw new Error('No profile ID available');
    }
    return await apiCall(`/progress/profile-stats/${effectiveProfileId}`);
  },
};

// ==================== GAMIFICATION API ====================

export const gamificationAPI = {
  getStreakFreezeStatus: async () => {
    return await apiCall('/gamification/streak-freeze/status');
  },
  
  useStreakFreeze: async () => {
    return await apiCall('/gamification/streak-freeze/use', {
      method: 'POST',
    });
  },
};

// ==================== LEADERBOARD API ====================

export const leaderboardAPI = {
  get: async (courseSlug = null) => {
    const query = courseSlug ? `?course_slug=${courseSlug}` : '';
    return await apiCall(`/leaderboard${query}`);
  },
};

export const paymentsAPI = {
  createCheckout: async (billingCycle) => {
    return await apiCall('/payments/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ billing_cycle: billingCycle }),
    });
  },
};

export default {
  auth: authAPI,
  courses: coursesAPI,
  progress: progressAPI,
  gamification: gamificationAPI,
  leaderboard: leaderboardAPI,
  payments: paymentsAPI,
};
